<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M6.
 * The assistant-edit settings form: name/description/model/temperature, the two prompt fields
 * (reply_instructions/restrictions — plain text, since they are interpolated verbatim into the
 * LLM system prompt), and the feature toggles. Emits `submit` with the API payload; the parent page
 * owns the create-vs-update call. Composed from the design-system field components (Input, TextArea,
 * Select, Switch) so focus/error states match the rest of the dashboard. Tailwind-only.
 */
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaModelsAPI from 'dashboard/api/eva/models';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: { type: Object, default: () => ({}) },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

// single source for the feature toggles (key + literal-keyed labels so the i18n extractor sees them
// and the linter stays quiet); populate/onSubmit derive the keys from here.
const featureFields = computed(() => [
  {
    key: 'feature_faq',
    label: t('EVA_ASSISTANT_SETTINGS.FEATURE_FAQ'),
    hint: t('EVA_ASSISTANT_SETTINGS.FEATURE_FAQ_HINT'),
  },
  {
    key: 'feature_memory',
    label: t('EVA_ASSISTANT_SETTINGS.FEATURE_MEMORY'),
    hint: t('EVA_ASSISTANT_SETTINGS.FEATURE_MEMORY_HINT'),
  },
  {
    key: 'feature_auto_resolve',
    label: t('EVA_ASSISTANT_SETTINGS.FEATURE_AUTO_RESOLVE'),
    hint: t('EVA_ASSISTANT_SETTINGS.FEATURE_AUTO_RESOLVE_HINT'),
  },
]);

const state = reactive({
  name: '',
  description: '',
  model: '',
  temperature: '',
  replyInstructions: '',
  restrictions: '',
  feature_faq: false,
  feature_memory: false,
  feature_auto_resolve: false,
});
const nameError = ref('');

// The curated model list for the picker (D2). Falls back to just the empty "(default)" option if the
// fetch fails, so the form still works.
const models = ref([]);
const defaultModel = ref('');

const loadModels = async () => {
  try {
    const { data } = await EvaModelsAPI.get();
    models.value = data.models;
    defaultModel.value = data.default;
  } catch {
    models.value = [];
  }
};

const modelOptions = computed(() => [
  {
    value: '',
    label: t('EVA_ASSISTANT_SETTINGS.MODEL_DEFAULT', {
      model: defaultModel.value,
    }),
  },
  ...models.value.map(model => ({ value: model, label: model })),
]);

// A pinned model outside the account's entitlement is clamped at every call (Eva::Assistant#model),
// so the picker goes on showing a model that never runs — the tenant believes it is on the model it
// chose while a weaker one answers. Resolves to the model that actually runs, or '' when there is
// nothing to warn about. Gated on the picker still matching what was saved: once the author changes
// the selection the warning is about a config that no longer exists on screen.
const modelDowngrade = computed(() => {
  if (!props.assistant?.model_downgraded) return '';
  return state.model === (props.assistant?.config?.model || '')
    ? props.assistant.effective_model
    : '';
});

onMounted(loadModels);

const populate = assistant => {
  const config = assistant?.config || {};
  state.name = assistant?.name || '';
  state.description = assistant?.description || '';
  state.model = config.model || '';
  // blank (not a hardcoded number) so an untouched field omits the key on submit and the
  // assistant runs at the backend's DEFAULT_TEMPERATURE — mirrors the model picker's
  // leave-blank-for-the-default convention (see MODEL_HINT).
  state.temperature = config.temperature ?? '';
  state.replyInstructions = assistant?.reply_instructions || '';
  state.restrictions = assistant?.restrictions || '';
  featureFields.value.forEach(({ key }) => {
    state[key] = config[key] || false;
  });
};

watch(() => props.assistant, populate, { immediate: true, deep: true });

const onSubmit = () => {
  if (!state.name.trim()) {
    nameError.value = t('EVA_ASSISTANT_SETTINGS.NAME_REQUIRED');
    return;
  }
  nameError.value = '';
  const config = {
    model: state.model,
    ...Object.fromEntries(
      featureFields.value.map(({ key }) => [key, state[key]])
    ),
  };
  // Always send temperature (like model, above): a cleared field (Number('') === 0) must fall back
  // to the backend default, not store a literal 0, AND must be able to CLEAR a previously-stored
  // override on an existing assistant — so send the blank string rather than omitting the key
  // entirely (omitting it would leave an old stored value untouched on update).
  const temperature = `${state.temperature}`.trim();
  config.temperature = temperature === '' ? '' : Number(temperature);
  emit('submit', {
    name: state.name.trim(),
    description: state.description,
    reply_instructions: state.replyInstructions,
    restrictions: state.restrictions,
    config,
  });
};

defineExpose({ onSubmit, loadModels });
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <!-- Basics -->
    <section
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('EVA_ASSISTANT_SETTINGS.BASICS') }}
      </h3>
      <Input
        v-model="state.name"
        :label="t('EVA_ASSISTANT_SETTINGS.NAME')"
        :placeholder="t('EVA_ASSISTANT_SETTINGS.NAME_PLACEHOLDER')"
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
      />
      <Input
        v-model="state.description"
        :label="t('EVA_ASSISTANT_SETTINGS.DESCRIPTION')"
        :placeholder="t('EVA_ASSISTANT_SETTINGS.DESCRIPTION_PLACEHOLDER')"
      />
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('EVA_ASSISTANT_SETTINGS.MODEL') }}
          </span>
          <Select
            v-model="state.model"
            :options="modelOptions"
            class="!w-full [&>select]:w-full"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('EVA_ASSISTANT_SETTINGS.MODEL_HINT') }}
          </span>
          <span v-if="modelDowngrade" class="text-xs text-n-amber-11">
            {{
              t('EVA_ASSISTANT_SETTINGS.MODEL_DOWNGRADED', {
                model: modelDowngrade,
              })
            }}
          </span>
        </label>
        <Input
          v-model="state.temperature"
          type="number"
          min="0"
          max="2"
          :label="t('EVA_ASSISTANT_SETTINGS.TEMPERATURE')"
          :message="t('EVA_ASSISTANT_SETTINGS.TEMPERATURE_HINT')"
        />
      </div>
    </section>

    <!-- Behaviour (verbatim prompt fields) -->
    <section
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('EVA_ASSISTANT_SETTINGS.BEHAVIOUR') }}
      </h3>
      <TextArea
        v-model="state.replyInstructions"
        auto-height
        min-height="6rem"
        max-height="20rem"
        :label="t('EVA_ASSISTANT_SETTINGS.REPLY_INSTRUCTIONS')"
        :placeholder="
          t('EVA_ASSISTANT_SETTINGS.REPLY_INSTRUCTIONS_PLACEHOLDER')
        "
        :message="t('EVA_ASSISTANT_SETTINGS.REPLY_INSTRUCTIONS_HINT')"
      />
      <TextArea
        v-model="state.restrictions"
        auto-height
        min-height="6rem"
        max-height="20rem"
        :label="t('EVA_ASSISTANT_SETTINGS.RESTRICTIONS')"
        :placeholder="t('EVA_ASSISTANT_SETTINGS.RESTRICTIONS_PLACEHOLDER')"
        :message="t('EVA_ASSISTANT_SETTINGS.RESTRICTIONS_HINT')"
      />
    </section>

    <!-- Features -->
    <section
      class="flex flex-col gap-2 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('EVA_ASSISTANT_SETTINGS.FEATURES') }}
      </h3>
      <div class="flex flex-col divide-y divide-n-weak">
        <div
          v-for="flag in featureFields"
          :key="flag.key"
          class="flex items-start justify-between gap-4 py-3"
        >
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ flag.label }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ flag.hint }}
            </span>
          </div>
          <Switch v-model="state[flag.key]" />
        </div>
      </div>
    </section>

    <div class="flex justify-end">
      <Button
        :label="t('EVA_ASSISTANT_SETTINGS.SAVE')"
        :is-loading="isSaving"
        :disabled="isSaving"
        @click="onSubmit"
      />
    </div>
  </div>
</template>
